import {
  connect as connectEmbeddedWallet,
  disconnect,
  getAccounts,
  getChainId,
  getProvider,
  initialize,
  isAuthorized,
  PROD_OPENSEA_WALLET_URL,
  switchChain,
} from "@opensea/embedded-wallet-sdk"
import type { EVMAddress } from "@opensea/utils"
import type { Hex } from "viem"
import { hexToNumber } from "viem"
import type { CreateConnectorFn, PartializedState } from "wagmi"
import { createConnector } from "wagmi"
import { OPENSEA_LOGO_URL } from "../assets"

export const EMBEDDED_WALLET_CONNECTOR_ID = "embeddedWallet"

type ConnectorResult<T extends boolean> = {
  accounts: T extends true
    ? readonly {
        address: EVMAddress
        capabilities: Record<string, unknown>
      }[]
    : readonly EVMAddress[]
  chainId: number
}

type EmbeddedWalletConnectorOptions = {
  url?: string
  debug?: boolean
}

export function embeddedWalletConnector({
  url = PROD_OPENSEA_WALLET_URL,
  debug = false,
}: EmbeddedWalletConnectorOptions = {}): CreateConnectorFn {
  return createConnector(config => {
    const onChainChanged = (chainId: string) => {
      config.emitter.emit("change", {
        chainId: hexToNumber(chainId as Hex),
      })
    }
    // Skip initial wagmi setup call to avoid initializing on page load
    let shouldInitialize = false
    return {
      id: EMBEDDED_WALLET_CONNECTOR_ID,
      icon: OPENSEA_LOGO_URL,
      name: "Embedded Wallet",
      setup: async () => {
        const store = (await config.storage?.getItem(STORE_KEY)) as
          | {
              state?: PartializedState
            }
          | undefined
        const wasPreviouslyConnected = Boolean(
          Array.from(store?.state?.connections?.values() ?? []).find(
            c => c.connector.id === EMBEDDED_WALLET_CONNECTOR_ID,
          ),
        )
        if (wasPreviouslyConnected || shouldInitialize) {
          return initialize({ url, debug })
        }
        shouldInitialize = true
        return Promise.resolve()
      },
      connect: async <TWithCapabilities extends boolean = false>({
        withCapabilities,
      }: {
        withCapabilities?: TWithCapabilities | boolean
      } = {}): Promise<ConnectorResult<TWithCapabilities>> => {
        const { accounts, chainId } = await connectEmbeddedWallet()
        const provider = await getProvider()
        if (!provider) {
          throw new Error("Provider not found after connecting")
        }
        provider.on("chainChanged", onChainChanged)

        if (withCapabilities === true) {
          const accountsWithCapabilities = accounts.map(address => ({
            address,
            capabilities: {},
          })) as readonly {
            address: EVMAddress
            capabilities: Record<string, unknown>
          }[]
          return {
            accounts: accountsWithCapabilities,
            chainId,
          } as ConnectorResult<TWithCapabilities>
        }

        return {
          accounts,
          chainId,
        } as ConnectorResult<TWithCapabilities>
      },
      disconnect,
      getAccounts,
      getChainId,
      getProvider,
      isAuthorized: async () => {
        const authorized = await isAuthorized()
        return authorized
      },
      switchChain: async (params: { chainId: number }) => {
        await switchChain(params.chainId)
        const chain = config.chains.find(c => c.id === params.chainId)
        if (!chain) {
          throw new Error("Tried to switch to an unsupported chain")
        }
        return chain
      },
      type: "embedded",
      onAccountsChanged: (accounts: EVMAddress[]): void => {
        if (accounts.length > 0) {
          disconnect()
          return
        }
        config.emitter.emit("change", {
          accounts,
        })
      },
      onChainChanged,
      onDisconnect: (): void => {
        config.emitter.emit("disconnect")
      },
    }
  })
}

const STORE_KEY = "store"
